import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { ErreurApi } from '../../lib/api';
import { useModifierFinImmersion, type LigneImmersion } from '../../lib/useMatching';
import { useToast } from '../ui/useToast';

function jourSaisi(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(new Date(iso));
}

/**
 * Fin d'une immersion pas encore tranchée : l'entreprise se décide plus tôt,
 * ou prolonge. L'API refuse un chevauchement avec une autre immersion et une
 * fin avant le début ; son message s'affiche tel quel.
 */
export function ModificationFinImmersion({
  immersion,
  onModifiee,
}: {
  immersion: LigneImmersion;
  onModifiee?: (immersion: LigneImmersion) => void;
}): JSX.Element | null {
  const notifier = useToast();
  const modifier = useModifierFinImmersion();
  const [ouverte, setOuverte] = useState(false);
  const [fin, setFin] = useState(immersion.dateFin ? jourSaisi(immersion.dateFin) : '');

  if (immersion.refusModificationFin !== null) return null;

  if (!ouverte) {
    return (
      <button
        type="button"
        onClick={() => setOuverte(true)}
        className="inline-flex min-h-[38px] items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-2 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[40px]"
      >
        <CalendarClock className="h-3.5 w-3.5" aria-hidden />
        Modifier la fin
      </button>
    );
  }

  const enregistrer = (): void =>
    modifier.mutate(
      { id: immersion.id, dateFin: fin },
      {
        onSuccess: (ligne) => {
          notifier('Fin de l’immersion modifiée.');
          setOuverte(false);
          onModifiee?.(ligne);
        },
      },
    );

  return (
    <div className="w-full space-y-2 rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="block min-w-0 flex-1">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Nouvelle fin
          </span>
          <input
            type="date"
            value={fin}
            min={immersion.dateDebut ? jourSaisi(immersion.dateDebut) : undefined}
            onChange={(evenement) => setFin(evenement.target.value)}
            disabled={modifier.isPending}
            className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
          />
        </label>
        <button
          type="button"
          onClick={enregistrer}
          disabled={modifier.isPending || !fin}
          className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
        >
          {modifier.isPending ? 'Enregistrement…' : 'Enregistrer la fin'}
        </button>
        <button
          type="button"
          onClick={() => setOuverte(false)}
          disabled={modifier.isPending}
          className="min-h-[44px] rounded-xl border border-gray-200 px-4 text-sm font-semibold text-codialis-gray transition hover:bg-gray-50"
        >
          Annuler
        </button>
      </div>
      {modifier.error instanceof ErreurApi && (
        <p role="alert" className="text-[11px] text-red-700">
          {modifier.error.message}
        </p>
      )}
    </div>
  );
}
